import React, { useState } from "react";
import { useNavigate } from "react-router";
import Bpx from "@mui/material/Box";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import Badge from "@mui/material/Badge";
import { Plus as PlusIcon } from "react-feather";
import Footer from "src/components/wizard/Footer/Footer";
import ItemDetails from "src/components/capture/ReceivingWizard/Items/ItemDetails/ItemDetails";
import useQueryParams from "src/hooks/useQueryParams";
// import CalculateItemWeight from "src/components/capture/ReceivingWizard/Items/CalculateItemWeight/CalculateItemWeight";
import getTotalReceivingUnitsLabel from "src/components/capture/ReceivingWizard/Shipment/TotalReceivingUnits/getTotalReceivingUnitsLabel";
import ConfirmPalletQuantities from "src/components/capture/ReceivingWizard/Items/ConfirmPalletQuantities/ConfirmPalletQuantities";
import MoreOptions from "./MoreOptions";
import Empty from "./Empty";
import Header from "../../../wizard/Header";
import SearchButton from "../../../wizard/SearchButton";
import SearchTextField from "../../../wizard/SearchTextField";
import ShipmentItem from "./ShipmentItem";
import ItemLookup from "./ItemLookup/ItemLookup";
import ReceivedItemsModal from "./ReceivedItemsModal/ReceivedItemsModal";
import OnsiteInventoryToWarehouseStorageModal from "./OnsiteInventoryToWarehouseStorageModal/OnsiteInventoryToWarehouseStorageModal";
import OnsiteManifestModal from "./OnsiteManifestModal/OnsiteManifestModal";
import BulkInputLocationModal from "./BulkInputLocationModal";
import useShipmentActions from "./hooks/useShipmentActions";
import useAuth from "src/hooks/useAuth";
import useInventoryItemsFilter from "src/hooks/useInventoryItemsFilter";
import {
  BY_THE_PALLET_WAREHOUSE_BUILT,
  HUNDREDWEIGHT,
  BY_THE_PALLET,
  BY_THE_UNIT_ITEM,
  BY_THE_VAULT,
  TIME_AND_MATERIALS_BLENDED,
  TIME_AND_MATERIALS_ITEMIZED,
} from "src/components/project/settings/utils/billingTypes";
import InboundBillingTypeSelectionModal from "../Shipment/Billing/InboundBillingTypeSelectionModal";
import BillingQuantitiesModal from "../Shipment/Billing/BillingQuantitiesModal";
import useMutation from "../../../../hooks/useMutation";
import { UPDATE_SHIPMENT_MUTATION } from "../../../../constants/graphql/mutations";
import {
  updateShipmentBillingType,
  addShipmentBillingToAssetInput,
  extractBillingTypes,
} from "../../../../utils/billingHelpers";
import useReceivingWizardFlow from "../hooks/useReceivingWizardFlow";
import WeightMismatchAlert from "../WeightMismatchAlert";

const getSameLocation = (shipmentItems, shipmentId, selectedIndex, sameLocation) => {
  if (!sameLocation) return null;

  const locations = shipmentItems
    .at(selectedIndex)
    ?.asset?.rackLocations.filter(v => v.shipment_id === shipmentId);

  if (locations && locations.length > 0) {
    return locations.slice(0, 1).map(v => {
      const loc = {
        warehouse: v.warehouse,
        warehouse_locations: v.warehouse_locations || [],
        shipment_id: v.shipment_id,
        sub_qty: 0,
        allocated_quantity: 0,
      };

      if (v.mobile_warehouse_location?.id) {
        loc.mobile_warehouse_location = v.mobile_warehouse_location;
      }

      return loc;
    });
  }

  return null;
};

export default function Items({ project, shipment, onClose, verticalLayout = false }) {
  const [openSearch, setOpenSearch] = useState(false);
  const navigate = useNavigate();
  const params = useQueryParams();
  const { user } = useAuth();
  const { settings } = user.company;
  const { hasAdditionalCharges, itemsNextStep, totalSteps } = useReceivingWizardFlow(project);
  const assetId = params.get("assetId");
  const shipmentId = params.get("shipmentId");
  const itemLookup = params.get("itemLookup") === "true";
  const receivedItems = params.get("receivedItems") === "true";
  const onsiteInventory = params.get("onsiteInventory") === "true";
  const bulkInputLocation = params.get("bulkInputLocation") === "true";
  const onsiteManifest = params.get("onsiteManifest") === "true";
  const shipmentItemId = params.get("shipmentItemId");
  const openItemWeight = params.get("itemWeight") === "true";
  const openPalletQuantities = params.get("palletQuantities") === "true";
  const openPalletWarehouseBuilt = params.get("palletWarehouseBuilt") === "true";
  const openTotalPalletsReceived = params.get("totalPalletsReceived") === "true";
  const openTotalUnitsItemsReceived = params.get("totalUnitsItemsReceived") === "true";
  const openTotalVaultsUsed = params.get("totalVaultsUsed") === "true";
  const openInboundBilling = params.get("inboundBilling") === "true";
  const billingStep = params.get("billingStep") || "1";
  const allShipmentItems = shipment?.shipmentItems || [];
  const totalPieces = shipment?.totalPieces || [];
  const isDraft = shipment?.draft;
  const { createShipment, createAssetLoading } = useShipmentActions(shipment.id, project?.id);
  const [updateShipment] = useMutation(UPDATE_SHIPMENT_MUTATION);
  const [selected, setSelected] = useState(null);
  const [enteredBillingAtStep2, setEnteredBillingAtStep2] = useState(false);
  const isOnsiteCaptureShipment =
    project?.settings?.onsite_capture_to_warehouse_storage && shipment?.shipmentStatus === "onsite_inventory";

  const {
    searchTerm,
    filteredItems: shipmentItems,
    handleSearchChange,
    clearSearch,
  } = useInventoryItemsFilter(allShipmentItems);

  const billingInitKey = `billing_initialized_${shipment.id}`;
  const getBillingInitialized = () => sessionStorage.getItem(billingInitKey) === "true";
  const setBillingInitialized = value => {
    if (value) {
      sessionStorage.setItem(billingInitKey, "true");
    } else {
      sessionStorage.removeItem(billingInitKey);
    }
  };

  // Clear billing initialization when component unmounts or when leaving items step
  React.useEffect(() => {
    return () => {
      sessionStorage.removeItem(billingInitKey);

      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith("billing_initialized_")) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
      });
    };
  }, [billingInitKey]);

  const selectedShipmentItem =
    allShipmentItems.find(v => v.id === shipmentItemId) ||
    allShipmentItems.find(v => String(v.asset?.id) === String(assetId));
  const selectedShipmentItemId = shipmentItemId || selectedShipmentItem?.id;
  const selectedShipmentIndex = allShipmentItems.findIndex(v => v.id === selectedShipmentItemId);
  const sameLocation = getSameLocation(
    allShipmentItems,
    shipmentId,
    selectedShipmentIndex - 1,
    settings.receiving_wizard_same_location
  );

  const handleToggle = () => {
    const params = new URLSearchParams(location.search);
    params.delete("assetId");
    params.delete("shipmentItemId");
    params.delete("addShipmentItem");
    params.delete("itemLookup");
    params.delete("receivedItems");
    params.delete("receivedItemsSearch");
    params.delete("onsiteInventory");
    params.delete("bulkInputLocation");
    params.delete("onsiteManifest");
    params.delete("rackLocationsChanges");
    // Don't delete duplicate and newItem params - they need to persist until Save is clicked
    navigate({ search: params.toString() });
  };

  const handleNextStep = () => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const warehouses = shipment.warehousesFromRackLocations || [];
    const allWarehousesExcluded = warehouses.length > 0 && warehouses.every(w => w.excludeFromBilling);
    params.set("step", hasAdditionalCharges && !allWarehousesExcluded ? "additional-charges" : itemsNextStep);
    params.delete("palletWarehouseBuilt");
    navigate({ pathname, search: params.toString() });
  };

  const getInboundBillingTypes = () => {
    const inboundBilling = project?.billingTypes?.inbound;
    return extractBillingTypes(inboundBilling, project?.billingType);
  };

  const hasSavedBillingQuantities = () => {
    if (shipment.billingType === HUNDREDWEIGHT) return Number(shipment.totalWeight) > 0;

    const quantities = Array.isArray(shipment.billingQuantities) ? shipment.billingQuantities : [];
    return quantities.some(q => q.quantity_type && Number(q.quantity_count) > 0);
  };

  const handleContinue = () => {
    if (allShipmentItems.length === 0) {
      return handleNextStep();
    }

    // Check if we need to show billing quantities modal on exit. Closing the modal
    // without saving leaves it initialized, so also re-prompt until quantities are saved.
    const needsBillingQuantities = !getBillingInitialized() || !hasSavedBillingQuantities();
    if (!isDraft && needsBillingQuantities && shipment.billingType) {
      const billingType = shipment.billingType;
      const warehouses = shipment.warehousesFromRackLocations || [];
      const allWarehousesExcluded =
        warehouses.length > 0 && warehouses.every(w => w.excludeFromBilling);

      if (
        !allWarehousesExcluded &&
        [
          HUNDREDWEIGHT,
          BY_THE_PALLET_WAREHOUSE_BUILT,
          BY_THE_PALLET,
          BY_THE_UNIT_ITEM,
          BY_THE_VAULT,
          TIME_AND_MATERIALS_BLENDED,
          TIME_AND_MATERIALS_ITEMIZED,
        ].includes(billingType)
      ) {
        setBillingInitialized(true);
        setEnteredBillingAtStep2(true);
        const params = new URLSearchParams(window.location.search);
        params.set("inboundBilling", "true");
        params.set("billingStep", "2");
        navigate({ search: params.toString() });
        return;
      }
    }

    /* Continue with existing weight calculation logic
    const params = new URLSearchParams(window.location.search);

    const billingTypes = [
      shipment.billingType,
      project.billingType,
      project?.billingTypes?.inbound,
    ];
    if (billingTypes.includes(HUNDREDWEIGHT)) {
      params.set("itemWeight", "true");
    } else {
      return handleNextStep();
    }

    navigate({ search: params.toString() });
    */
    return handleNextStep();
  };

  const handleBack = () => {
    setBillingInitialized(false);

    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("billing_initialized_")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
    });

    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.set("step", "shipment");
    navigate({ pathname, search: params.toString() });
  };

  const handleSelect = value => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(location.search);
    params.set("shipmentItemId", value.id);
    params.set("assetId", value.asset.id);
    navigate({ pathname, search: params.toString() });
    setSelected(value.id);
  };

  const handleCreateShipment = (values = {}) => {
    const { duplicate, ...assetValues } = values;
    const input = addShipmentBillingToAssetInput(shipment, assetValues);

    createShipment(input, { duplicate });
  };

  const handleAddItem = () => {
    const pathname = window.location.pathname;
    const newParams = new URLSearchParams(location.search);

    if (project?.settings?.onsite_manifest) {
      newParams.set("onsiteManifest", "true");
      navigate({ pathname, search: newParams.toString() });
    } else if (project.importedAssets || project.referenceAssets) {
      newParams.set("itemLookup", "true");
      navigate({ pathname, search: newParams.toString() });
    } else {
      handleCreateShipment();
    }
  };

  const handleClosePalletsBuilt = () => {
    const params = new URLSearchParams(location.search);
    params.delete("palletWarehouseBuilt");
    params.delete("totalPalletsReceived");
    params.delete("totalUnitsItemsReceived");
    params.delete("totalVaultsUsed");
    navigate({ search: params.toString() });
  };

  const handleBillingTypeSelect = async billingType => {
    try {
      // Handle "I'm not sure" selection - save TBD and clear billing_quantities, then close modal
      if (billingType === "I'm not sure") {
        await updateShipmentBillingType(updateShipment, shipment, "TBD", { billingQuantities: [] });
        handleCloseBilling();
        return;
      }

      // Save the selected billing type to the shipment
      await updateShipmentBillingType(updateShipment, shipment, billingType);

      const params = new URLSearchParams(location.search);
      params.set("billingStep", "2");
      navigate({ search: params.toString() });
    } catch (error) {
      console.error("Error updating shipment billing type:", error);
    }
  };

  const handleBillingBack = () => handleCloseBilling();

  const handleCloseBilling = () => {
    setEnteredBillingAtStep2(false);
    const params = new URLSearchParams(location.search);
    params.delete("inboundBilling");
    params.delete("billingStep");
    navigate({ search: params.toString() });
  };

  const handleBillingQuantitiesSave = () => {
    const params = new URLSearchParams(location.search);
    params.delete("inboundBilling");
    params.delete("billingStep");

    /* After billing is complete, continue with the normal flow
    const billingTypes = [
      shipment.billingType,
      project.billingType,
      project?.billingTypes?.inbound,
    ];
    if (billingTypes.includes(HUNDREDWEIGHT)) {
      params.set("itemWeight", "true");
      return navigate({ search: params.toString() });
    } else {
      // Go directly to next step
    }
    */
    params.set("step", hasAdditionalCharges ? "additional-charges" : itemsNextStep);
    params.delete("palletWarehouseBuilt");
    navigate({ pathname: window.location.pathname, search: params.toString() });
  };

  const handleBillingQuantitiesBack = () => {
    if (enteredBillingAtStep2) {
      setBillingInitialized(false);
      handleCloseBilling();
      return;
    }
    const params = new URLSearchParams(location.search);
    params.set("billingStep", "1");
    navigate({ search: params.toString() });
  };

  const handleToggleSearch = () => {
    if (openSearch) {
      clearSearch();
    }
    setOpenSearch(v => !v);
  };

  const handleReceiveIntoWarehouse = () => {
    const params = new URLSearchParams(location.search);
    params.set("bulkInputLocation", "true");
    navigate({ search: params.toString() });
  };

  return (
    <Bpx display="flex" flexDirection="column" justifyContent="space-between" minHeight="100%">
      <Header
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <span>Inbound Items</span>
            <Badge
              badgeContent={allShipmentItems.length || 0}
              max={999999}
              sx={{
                '& .MuiBadge-badge': {
                  position: 'relative',
                  transform: 'none',
                  backgroundColor: 'transparent',
                  color: '#666',
                  fontSize: '14px',
                  height: '24px',
                  minWidth: '24px',
                  borderRadius: '50%',
                  fontWeight: 500,
                  border: '1.5px solid #999',
                }
              }}
            />
          </Box>
        }
        actions={<SearchButton open={openSearch} onClick={handleToggleSearch} />}
        info={
          isOnsiteCaptureShipment ? (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleReceiveIntoWarehouse}
              sx={{
                borderWidth: 2,
                "&:hover": {
                  borderWidth: 2,
                },
              }}
            >
              Receive Into Warehouse
            </Button>
          ) : project.addPreviouslyReceivedAssetsToShipments ||
            project?.settings?.onsite_capture_to_warehouse_storage ||
            project?.settings?.onsite_manifest ? (
            <MoreOptions shipment={shipment} />
          ) : (
            getTotalReceivingUnitsLabel(totalPieces)
          )
        }
      />
      <WeightMismatchAlert shipment={shipment} />
      {openSearch && (
        <Box px={2} pt={2}>
          <SearchTextField value={searchTerm} onChange={handleSearchChange} />
        </Box>
      )}
      <Box p={2} flexGrow={1} sx={{ overflowY: "auto" }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          {allShipmentItems.length === 0 ? (
            <Empty onsiteManifest={!!project?.settings?.onsite_manifest} />
          ) : shipmentItems.length === 0 && searchTerm ? (
            <Box mt={6} pt={2} mb={3} display="flex" justifyContent="center" width="100%">
              <Typography variant="body2" color="textSecondary">
                No items found matching "{searchTerm}"
              </Typography>
            </Box>
          ) : (
            <Box width="100%" minHeight={300}>
              {shipmentItems.map(value => (
                <ShipmentItem
                  key={value.id}
                  value={value}
                  selected={selected === value.id}
                  project={project}
                  onSelect={handleSelect}
                  shipmentId={shipment.id}
                  shipment={shipment}
                />
              ))}
              {/*openItemWeight && (
                <CalculateItemWeight shipment={shipment} shipmentItems={allShipmentItems} />
              )*/}
              {openPalletQuantities && (
                <ConfirmPalletQuantities
                  shipment={shipment}
                  shipmentItems={allShipmentItems}
                  onNext={handleNextStep}
                />
              )}
            </Box>
          )}
          <Box mt={4}>
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={handleAddItem}
              disabled={createAssetLoading}
              startIcon={
                createAssetLoading ? (
                  <CircularProgress size={22} />
                ) : (
                  <SvgIcon fontSize="small">
                    <PlusIcon />
                  </SvgIcon>
                )
              }
            >
              Add Item
            </Button>
            {assetId && (
              <ItemDetails
                id={assetId}
                project={project}
                shipment={shipment}
                shipmentItem={selectedShipmentItem}
                sameLocation={sameLocation}
                onClose={handleToggle}
              />
            )}
            {itemLookup && (
              <ItemLookup
                project={project}
                createAssetLoading={createAssetLoading}
                onClose={handleToggle}
                onCreateAsset={handleCreateShipment}
              />
            )}

            {receivedItems && <ReceivedItemsModal project={project} onClose={handleToggle} />}

            {onsiteInventory && (
              <OnsiteInventoryToWarehouseStorageModal project={project} onClose={handleToggle} />
            )}

            {onsiteManifest && (
              <OnsiteManifestModal project={project} onClose={handleToggle} />
            )}

            {bulkInputLocation && (
              <BulkInputLocationModal
                open={true}
                onClose={handleToggle}
                shipmentItems={allShipmentItems}
                project={project}
                shipment={shipment}
                shipmentId={shipment.id}
              />
            )}

            {openInboundBilling &&
              billingStep === "1" &&
              (() => {
                const { types, comments } = getInboundBillingTypes();
                return (
                  <InboundBillingTypeSelectionModal
                    open={true}
                    onClose={handleCloseBilling}
                    billingTypes={types}
                    billingComments={comments}
                    onSelect={handleBillingTypeSelect}
                    selectedBillingType={shipment.billingType}
                    onBack={handleBillingBack}
                  />
                );
              })()}

            {openInboundBilling && billingStep === "2" && (
              <BillingQuantitiesModal
                type="Inbound"
                onClose={handleCloseBilling}
                billingType={shipment.billingType || getInboundBillingTypes().types[0]}
                shipment={shipment}
                palletTypes={project?.palletTypes}
                unitTypes={project?.unitTypes}
                onSave={handleBillingQuantitiesSave}
                onBack={handleBillingQuantitiesBack}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Box mt={2}>
        <Footer
          step={1}
          steps={totalSteps}
          startTime={verticalLayout ? null : shipment?.startTime}
          completeTime={verticalLayout ? null : shipment?.completeTime}
        >
          <div style={{ width: verticalLayout ? "100%" : "auto" }}>
            {!verticalLayout && (
              <Button onClick={handleBack} variant="outlined" sx={{ mr: 1, textTransform: "none" }}>
                Back
              </Button>
            )}
            <Button
              type="submit"
              disabled={allShipmentItems.length === 0 && !isDraft}
              variant="contained"
              fullWidth={verticalLayout}
              sx={{ textTransform: "none" }}
              onClick={handleContinue}
            >
              {verticalLayout ? "Next Step" : "Next"}
            </Button>
          </div>
        </Footer>
      </Box>
    </Bpx>
  );
}
